import * as Yup from 'yup';
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { LoadingButton } from '@mui/lab';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Box,
  Grid,
  Alert,
  Stack,
  alpha,
  Button,
  Dialog,
  Divider,
  MenuItem,
  useTheme,
  IconButton,
  Typography,
  useMediaQuery,
  CircularProgress,
} from '@mui/material';

import { useAuthContext } from 'src/hooks/auth/use-auth-context';

import { paper } from 'src/theme/css';
import { useTranslate } from 'src/locales';
import { EmailConnectionStatus } from 'src/services/email-sync/email-sync.types';
import { generateScheduleMeetingError } from 'src/services/calendar/calendar.utils';
import { useGetEmailConnectionStateQuery } from 'src/services/email-sync/email-sync.service';
import { useScheduleMeetingMutation, useGetMyBusyIntervalsQuery } from 'src/services/calendar/calendar.service';

import Iconify from 'src/components/iconify';
import FormProvider, { RHFSelect, RHFTextField } from 'src/components/hook-form';
import FeatureLockedListView from 'src/components/billing/feature-locked-list-view';

import { useFeatures } from 'src/sections/billing/usage/hooks/use-features';
import ConnectEmailPlaceholder from 'src/sections/account/components/connect-email-placeholder';
import MeetingOptionsFields, {
  MeetingOptionsValues,
  MeetingAttendeeOption,
  toScheduleMeetingAttendees,
} from 'src/sections/calendar/components/meeting-options-fields';

import { FeatureType } from 'src/types/subscription.types';

import {
  MeetingSlotPeriod,
  buildMeetingSlots,
  MEETING_SLOT_PERIODS,
  MEETING_DURATION_OPTIONS,
} from './schedule-meeting.utils';

// ----------------------------------------------------------------------

export type ScheduleMeetingInitialAttendee = MeetingAttendeeOption & {
  type: 'contact' | 'candidate';
};

interface ScheduleMeetingModalProps {
  open: boolean;
  onClose: () => void;
  initialAttendee?: ScheduleMeetingInitialAttendee;
}

type FormValues = MeetingOptionsValues & {
  title: string;
  description: string;
  duration_minutes: number;
  location: string;
};

const PERIOD_ICONS: Record<MeetingSlotPeriod, string> = {
  overnight: 'eva:moon-fill',
  morning: 'eva:sun-outline',
  afternoon: 'eva:sun-fill',
  evening: 'eva:moon-outline',
};

export default function ScheduleMeetingModal({
  open,
  onClose,
  initialAttendee,
}: ScheduleMeetingModalProps) {

  const theme = useTheme();

  const { t } = useTranslate();

  const { enqueueSnackbar } = useSnackbar();

  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));

  const { account } = useAuthContext();

  const { canAccess } = useFeatures();

  const timezone = account.timezone || moment.tz.guess();

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlotKey, setSelectedSlotKey] = useState<string | null>(null);

  const { currentData: connectionState, isLoading: isConnectionLoading } = useGetEmailConnectionStateQuery();

  const isEmailConnectionActive = connectionState?.status === EmailConnectionStatus.ACTIVE;

  const [scheduleMeeting, { isLoading: isScheduling }] = useScheduleMeetingMutation();

  const ScheduleMeetingSchema = Yup.object().shape({
    title: Yup.string().required(t('validation.required')),
    description: Yup.string(),
    duration_minutes: Yup.number().required(t('validation.required')),
    location: Yup.string(),
    attendees: Yup.array().min(1, t('validation.required')),
    online_meeting: Yup.boolean(),
    add_bot: Yup.boolean(),
    attending: Yup.boolean(),
  });

  const methods = useForm<FormValues>({
    resolver: yupResolver(ScheduleMeetingSchema) as any,
    defaultValues: {
      title: '',
      description: '',
      duration_minutes: 30,
      location: '',
      attendees: initialAttendee ? [initialAttendee] : [],
      online_meeting: false,
      add_bot: false,
      attending: true,
    },
  });

  const { watch, handleSubmit } = methods;

  const title = watch('title');
  const durationMinutes = watch('duration_minutes');
  const attendees = watch('attendees');
  const onlineMeeting = watch('online_meeting');
  const attending = watch('attending');

  // Always refetch: the busy list includes live Google/Outlook events that change outside JobTetris.
  const {
    currentData: busyIntervals,
    isFetching: isBusyFetching,
    isError: isBusyError,
  } = useGetMyBusyIntervalsQuery(
    { date: selectedDate as string, timezone },
    { skip: !open || !selectedDate || !attending, refetchOnMountOrArgChange: true }
  );

  const isCheckingBusy = attending && isBusyFetching;

  const slots = useMemo(() => {
    if (!selectedDate) return [];

    return buildMeetingSlots({
      date: selectedDate,
      timezone,
      durationMinutes: Number(durationMinutes),
      busyIntervals: attending ? busyIntervals ?? [] : [],
    });
  }, [selectedDate, timezone, durationMinutes, attending, busyIntervals]);

  // A longer duration or a busy result can rule out the chosen start; treat it as unselected then.
  const selectedSlot = slots.find((slot) => slot.key === selectedSlotKey && !slot.disabled) ?? null;

  const calendarValue = useMemo(
    () => (selectedDate ? moment.tz(selectedDate, 'YYYY-MM-DD', timezone) : null),
    [selectedDate, timezone]
  );

  const canSubmit = !!title?.trim() && !!selectedSlot && attendees.length > 0 && !isCheckingBusy;

  const onSubmit = handleSubmit(async (data) => {
    if (!selectedSlot) return;

    try {
      await scheduleMeeting({
        title: data.title,
        description: data.description || undefined,
        start_at: selectedSlot.start.toISOString(),
        end_at: selectedSlot.end.toISOString(),
        timezone,
        location: !data.online_meeting && data.location ? data.location : undefined,
        online_meeting: data.online_meeting,
        add_bot: data.online_meeting && data.add_bot,
        attending: data.attending,
        attendees: toScheduleMeetingAttendees(data.attendees),
      }).unwrap();

      enqueueSnackbar(t('calendar.schedule_meeting.api.success'), { variant: 'success' });
      onClose();
    } catch (error) {
      enqueueSnackbar(generateScheduleMeetingError(error), { variant: 'error' });
    }
  });

  const renderSlotGroup = (period: MeetingSlotPeriod) => {
    const periodSlots = slots.filter((slot) => slot.period === period);

    if (periodSlots.length === 0) return null;

    return (
      <Box key={period}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Iconify icon={PERIOD_ICONS[period]} width={18} />
          <Typography variant="subtitle2">
            {t(`calendar.schedule_meeting.periods.${period}`)}
          </Typography>
        </Stack>

        <Grid container spacing={1}>
          {periodSlots.map((slot) => {
            const selected = slot.key === selectedSlot?.key;

            return (
              <Grid item xs={4} sm={3} key={slot.key}>
                <Button
                  fullWidth
                  size="small"
                  disabled={slot.disabled}
                  variant={selected ? 'contained' : 'outlined'}
                  onClick={() => setSelectedSlotKey(slot.key)}
                  sx={{
                    py: 1,
                    textTransform: 'none',
                    fontWeight: selected ? 600 : 500,
                    ...(!selected && { borderColor: alpha(theme.palette.primary.main, 0.3) }),
                  }}
                >
                  {slot.start.format('h:mm A')}
                </Button>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  const renderDetails = (
    <Stack spacing={2.5}>
      <RHFTextField
        required
        autoFocus
        name="title"
        disabled={isScheduling}
        label={t('calendar.editor.title')}
      />

      <RHFTextField
        name="description"
        disabled={isScheduling}
        label={t('calendar.editor.description')}
        multiline
        rows={3}
      />

      <RHFSelect
        name="duration_minutes"
        disabled={isScheduling}
        label={t('calendar.schedule_meeting.duration')}
      >
        {MEETING_DURATION_OPTIONS.map((minutes) => (
          <MenuItem key={minutes} value={minutes}>
            {`${minutes} ${t('common.time.minutes')}`}
          </MenuItem>
        ))}
      </RHFSelect>

      {
        onlineMeeting === false && (
          <RHFTextField
            name="location"
            disabled={isScheduling}
            label={t('calendar.editor.location')}
          />
        )
      }

      <MeetingOptionsFields disabled={isScheduling} />
    </Stack>
  );

  const renderTimePicker = (
    <Stack spacing={2}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <DateCalendar
            disablePast
            value={calendarValue}
            timezone={timezone}
            disabled={isScheduling}
            onChange={(value) => {
              if (!value) return;
              setSelectedDate(moment(value).tz(timezone).format('YYYY-MM-DD'));
              setSelectedSlotKey(null);
            }}
            sx={{
              '& .MuiPickersCalendarHeader-root': {
                paddingLeft: 1,
                paddingRight: 1,
                minHeight: 40,
              },
              '& .MuiPickersArrowSwitcher-root': {
                display: 'inline-flex',
              },
              '& .MuiPickersCalendarHeader-label': {
                fontSize: '1.125rem',
                fontWeight: 600,
              },
              '& .MuiDayCalendar-weekDayLabel': {
                fontSize: '0.75rem',
                fontWeight: 600,
                color: theme.palette.text.secondary,
              },
              '& .MuiPickersDay-root': {
                fontSize: '0.875rem',
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
                '&.MuiPickersDay-today': {
                  border: `1px solid ${theme.palette.primary.main}`,
                  '&:not(.Mui-selected)': {
                    backgroundColor: 'transparent',
                  },
                },
              },
              '& .MuiPickersDay-root.Mui-disabled': {
                color: theme.palette.text.disabled,
              },
            }}
          />
        </Box>
      </LocalizationProvider>

      {!selectedDate && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Iconify icon="eva:calendar-outline" width={40} sx={{ color: 'text.disabled' }} />
          <Typography variant="body2" color="text.secondary">
            {t('calendar.schedule_meeting.pick_date_helper')}
          </Typography>
        </Box>
      )}

      {selectedDate && isCheckingBusy && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {selectedDate && !isCheckingBusy && (
        <Stack spacing={2}>
          {attending && isBusyError && (
            <Alert severity="warning">{t('calendar.schedule_meeting.busy_check_failed')}</Alert>
          )}
          {MEETING_SLOT_PERIODS.map((period) => renderSlotGroup(period))}
        </Stack>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
        {t('calendar.schedule_meeting.timezone_caption', { timezone })}
      </Typography>
    </Stack>
  );

  const renderBody = () => {
    if (!canAccess(FeatureType.CALENDAR_ENABLED)) {
      return (
        <Box sx={{ p: 3 }}>
          <FeatureLockedListView text={t('billing.subscription_required')} onNavigate={onClose} />
        </Box>
      );
    }

    if (isConnectionLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (!isEmailConnectionActive) {
      return (
        <Box sx={{ p: 3 }}>
          <ConnectEmailPlaceholder />
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 3 }}>
          <FormProvider methods={methods}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                {renderDetails}
              </Grid>
              <Grid item xs={12} md={6}>
                {renderTimePicker}
              </Grid>
            </Grid>
          </FormProvider>
        </Box>

        <Divider />

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ px: 3, py: 2 }}
        >
          <Typography variant="body2" color={selectedSlot ? 'text.primary' : 'text.secondary'}>
            {selectedSlot
              ? t('calendar.schedule_meeting.summary', {
                date: selectedSlot.start.format('ddd D MMM YYYY'),
                start: selectedSlot.start.format('h:mm A'),
                end: selectedSlot.end.format('h:mm A'),
                timezone,
              })
              : t('calendar.schedule_meeting.summary_empty')}
          </Typography>

          <LoadingButton
            variant="contained"
            loading={isScheduling}
            disabled={!canSubmit}
            onClick={onSubmit}
            startIcon={<Iconify icon="mdi:send" />}
          >
            {t('calendar.schedule_meeting.send_invites')}
          </LoadingButton>
        </Stack>
      </>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={isScheduling ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={isMobile}
      // Opened from the record panel overlay, whose own Dialog would otherwise hold focus and leave
      // the app-root sidesheets unusable (same reason as record-panel-frame).
      disableEnforceFocus
      PaperProps={{
        sx: {
          height: isMobile ? '100%' : '90vh',
          ...paper({ theme, bgcolor: theme.palette.background.default }),
        },
      }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 2 }}>
        <Typography variant="h6">{t('calendar.schedule_meeting.title')}</Typography>
        <IconButton size="small" onClick={onClose} disabled={isScheduling}>
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Stack>

      <Divider />

      {renderBody()}
    </Dialog>
  );
}
